import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3,
  TrendingUp,
  Users,
  ShoppingCart,
  Play,
  CheckCircle2,
  Smartphone,
  Monitor,
  Tablet,
  RefreshCw,
  DollarSign,
  Package,
  Lock,
  Zap
} from 'lucide-react';
import apiClient from '../utils/apiClient';
import { toast } from 'sonner';

const LockedFeature = ({ title, description, requiredPlan = "Professional", onUpgradeClick }) => {
  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gray-100 bg-opacity-80 backdrop-blur-sm rounded-lg z-10 flex flex-col items-center justify-center p-6">
        <Lock className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 text-center mb-4">{description}</p>
        <Button 
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          onClick={onUpgradeClick}
        >
          <Zap className="h-4 w-4 mr-2" />
          Upgrade to {requiredPlan}
        </Button>
      </div>
      <div className="blur-sm pointer-events-none opacity-50">
        {/* Placeholder content */}
        <div className="h-48 bg-gray-100 rounded-lg"></div>
      </div>
    </div>
  );
};

const AnalyticsDashboard = ({ subscription, onUpgradeClick, fullAnalyticsOverride = false }) => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30); // Default: last 30 days
  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState([]);

  const planName = subscription?.plan_name?.toLowerCase() || 'free';
  const isStarter = !fullAnalyticsOverride && planName === 'starter';
  const isProfessional = planName === 'professional';
  const isEnterprise = planName === 'enterprise';
  const hasAdvancedAnalytics = fullAnalyticsOverride || isProfessional || isEnterprise;

  // Debug logging
  console.log('Analytics Dashboard - Subscription:', subscription);
  console.log('Analytics Dashboard - Plan Name:', planName);
  console.log('Analytics Dashboard - Is Starter:', isStarter);
  console.log('Analytics Dashboard - Has Advanced:', hasAdvancedAnalytics);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, productsRes] = await Promise.all([
        apiClient.get(`/analytics/overview?days=${dateRange}`),
        apiClient.get(`/analytics/products?days=${dateRange}&limit=10`)
      ]);

      setOverview(overviewRes.data);
      setProducts(productsRes.data.products || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Free tier - show upgrade prompt (bypassed when full_analytics flag is on)
  if (planName === 'free' && !fullAnalyticsOverride) {
    return (
      <div className="text-center py-12">
        <Lock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Analytics Available with Paid Plans</h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Unlock powerful analytics to track audio performance, customer engagement, and purchase conversion rates.
        </p>
        <Button 
          size="lg"
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          onClick={onUpgradeClick}
        >
          <Zap className="h-5 w-5 mr-2" />
          View Plans & Pricing
        </Button>
      </div>
    );
  }

  const deviceBreakdown = overview?.device_breakdown || {};
  const totalDeviceEvents = deviceBreakdown.mobile + deviceBreakdown.desktop + deviceBreakdown.tablet;

  return (
    <div className="space-y-6">
      {/* Header with Date Range Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics & ROI</h2>
          <p className="text-sm text-gray-600">Track audio performance and customer engagement</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={dateRange === 7 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange(7)}
          >
            7 Days
          </Button>
          <Button
            variant={dateRange === 30 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange(30)}
          >
            30 Days
          </Button>
          <Button
            variant={dateRange === 365 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange(365)}
          >
            All Time
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadAnalytics}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Today's Real-Time Stats - Starter: Show only plays */}
      {isStarter && overview?.today && (
        <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0">
          <CardHeader>
            <CardTitle className="text-white">📊 Today's Activity</CardTitle>
            <CardDescription className="text-indigo-100">
              Your audio playback stats
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-sm opacity-90 mb-2">Audio Plays</p>
              <p className="text-5xl font-bold">{overview.today.plays}</p>
              <p className="text-sm opacity-75 mt-4">
                🔒 Upgrade to Professional to see completions, purchases & ROI
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Real-Time Stats - Professional/Enterprise: Show all */}
      {hasAdvancedAnalytics && overview?.today && (
        <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0">
          <CardHeader>
            <CardTitle className="text-white">📊 Today's Activity (Real-Time)</CardTitle>
            <CardDescription className="text-indigo-100">
              Live stats from your storefront
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm opacity-90">Plays</p>
                <p className="text-3xl font-bold">{overview.today.plays}</p>
              </div>
              <div>
                <p className="text-sm opacity-90">Completions</p>
                <p className="text-3xl font-bold">{overview.today.completions}</p>
              </div>
              <div>
                <p className="text-sm opacity-90">Cart Adds</p>
                <p className="text-3xl font-bold">{overview.today.cart_adds}</p>
              </div>
              <div>
                <p className="text-sm opacity-90">Purchases</p>
                <p className="text-3xl font-bold">{overview.today.purchases || 0}</p>
              </div>
              <div>
                <p className="text-sm opacity-90">With Audio</p>
                <p className="text-3xl font-bold">{overview.today.purchases_with_audio || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics Cards - Starter: Only Total Plays */}
      {isStarter && (
        <>
          <div className="grid grid-cols-1 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Plays</CardTitle>
                <Play className="h-4 w-4 text-indigo-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overview?.metrics.total_plays || 0}</div>
                <p className="text-xs text-gray-600 mt-1">
                  Audio playbacks in last {dateRange} days
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Locked Features for Starter */}
          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-purple-600" />
                Unlock Advanced Analytics
              </CardTitle>
              <CardDescription>
                Upgrade to Professional to access purchase tracking & ROI metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 opacity-60">
                    <CheckCircle2 className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm font-medium">Completion Rate</p>
                    <p className="text-xs text-gray-500">See listening behavior</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 opacity-60">
                    <ShoppingCart className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm font-medium">Cart Additions</p>
                    <p className="text-xs text-gray-500">Track add-to-cart events</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 opacity-60">
                    <Package className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm font-medium">Purchases</p>
                    <p className="text-xs text-gray-500">Real purchase tracking</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 opacity-60">
                    <DollarSign className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm font-medium">ROI Analytics</p>
                    <p className="text-xs text-gray-500">Prove audio value</p>
                  </div>
                </div>
                <Button 
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  onClick={onUpgradeClick}
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Upgrade to Professional - ~~$79.99~~ $39.99/month
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Key Metrics Cards - Professional/Enterprise: All Metrics */}
      {hasAdvancedAnalytics && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Plays</CardTitle>
            <Play className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.metrics.total_plays || 0}</div>
            <p className="text-xs text-gray-600 mt-1">
              Audio playbacks started
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.metrics.completion_rate || 0}%</div>
            <p className="text-xs text-gray-600 mt-1">
              {overview?.metrics.total_completions || 0} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cart Additions</CardTitle>
            <ShoppingCart className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.metrics.total_cart_adds || 0}</div>
            <p className="text-xs text-gray-600 mt-1">
              After audio playback
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Purchases</CardTitle>
            <Package className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.metrics.purchases_with_audio || 0}</div>
            <p className="text-xs text-gray-600 mt-1">
              With audio engagement
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Purchase Rate</CardTitle>
            <DollarSign className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.metrics.purchase_conversion_rate || 0}%</div>
            <p className="text-xs text-gray-600 mt-1">
              Purchases per play
            </p>
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {/* Audio Engagement Impact - Only for Professional/Enterprise */}
      {hasAdvancedAnalytics && overview?.metrics.total_purchases > 0 && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <TrendingUp className="h-5 w-5" />
              Audio Engagement Impact
            </CardTitle>
            <CardDescription className="text-green-700">
              How audio descriptions influence purchasing decisions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-sm text-green-700 mb-1">Total Purchases</p>
                <p className="text-4xl font-bold text-green-900">{overview.metrics.total_purchases}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-green-700 mb-1">With Audio Engagement</p>
                <p className="text-4xl font-bold text-green-900">{overview.metrics.purchases_with_audio}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-green-700 mb-1">Engagement Rate</p>
                <p className="text-4xl font-bold text-green-900">{overview.metrics.audio_engagement_rate || 0}%</p>
                <p className="text-xs text-green-600 mt-1">of purchasers listened to audio</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-white rounded-lg border border-green-200">
              <p className="text-sm text-gray-700">
                <strong className="text-green-800">💡 Insight:</strong> {overview.metrics.audio_engagement_rate >= 50 
                  ? "Great! More than half of your customers are engaging with audio before purchasing. This shows the value of your audio descriptions."
                  : "Consider promoting your audio feature more prominently to increase engagement and potentially boost conversions."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Device Breakdown - Only for Professional/Enterprise */}
      {hasAdvancedAnalytics && (
      <Card>
        <CardHeader>
          <CardTitle>Device Breakdown</CardTitle>
          <CardDescription>Audio plays by device type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5 text-indigo-600" />
                <span className="font-medium">Mobile</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full"
                    style={{ width: `${totalDeviceEvents > 0 ? (deviceBreakdown.mobile / totalDeviceEvents) * 100 : 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium w-12 text-right">{deviceBreakdown.mobile}</span>
                <Badge variant="secondary">
                  {totalDeviceEvents > 0 ? Math.round((deviceBreakdown.mobile / totalDeviceEvents) * 100) : 0}%
                </Badge>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Monitor className="h-5 w-5 text-green-600" />
                <span className="font-medium">Desktop</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${totalDeviceEvents > 0 ? (deviceBreakdown.desktop / totalDeviceEvents) * 100 : 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium w-12 text-right">{deviceBreakdown.desktop}</span>
                <Badge variant="secondary">
                  {totalDeviceEvents > 0 ? Math.round((deviceBreakdown.desktop / totalDeviceEvents) * 100) : 0}%
                </Badge>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Tablet className="h-5 w-5 text-purple-600" />
                <span className="font-medium">Tablet</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full"
                    style={{ width: `${totalDeviceEvents > 0 ? (deviceBreakdown.tablet / totalDeviceEvents) * 100 : 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium w-12 text-right">{deviceBreakdown.tablet}</span>
                <Badge variant="secondary">
                  {totalDeviceEvents > 0 ? Math.round((deviceBreakdown.tablet / totalDeviceEvents) * 100) : 0}%
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Top Performing Products - Only for Professional/Enterprise */}
      {hasAdvancedAnalytics && (
      <Card>
        <CardHeader>
          <CardTitle>Top Performing Products</CardTitle>
          <CardDescription>Products with most audio engagement</CardDescription>
        </CardHeader>
        <CardContent>
          {products.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No product data yet</p>
              <p className="text-sm">Data will appear once customers interact with audio</p>
            </div>
          ) : (
            <div className="space-y-4">
              {products.map((product, index) => (
                <div key={product.product_handle} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50">
                  <div className="text-2xl font-bold text-gray-400 w-8">#{index + 1}</div>
                  {product.product_image && (
                    <img
                      src={product.product_image}
                      alt={product.product_title}
                      className="w-12 h-12 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{product.product_title}</h4>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                      <span>🎵 {product.plays} plays</span>
                      <span>✅ {product.completion_rate}% completed</span>
                      <span>🛒 {product.cart_adds} cart adds</span>
                      {product.purchases_with_audio > 0 && (
                        <span className="font-semibold text-green-700">💰 {product.purchases_with_audio} purchases</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 mb-1">
                      {product.cart_conversion_rate}% cart rate
                    </Badge>
                    {product.purchase_conversion_rate > 0 && (
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        {product.purchase_conversion_rate}% purchase rate
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
